import { useCallback, useMemo, useRef, useState } from "react";

import { consultarCENT } from "../../services/rtoCent";
import { limpiar, mensajeDeConsulta } from "../../shared/rtoCent";

/* «Consultar ahora»: la RTO de UNA unidad, preguntada al CENT en el momento.
 *
 * Lo pidió Igna el 09/10 a raíz del mail de Aldo —«recién hicimos la VTV del
 * furgón 448 y no aparece habilitada en FIBOT»— (ticket #371). El dato de la
 * pantalla es el de la pasada del bot; esto es para el que acaba de volver de
 * la planta y no puede esperar a la siguiente.
 *
 * ── EL ESTADO VIVE AFUERA DE LA FILA, POR PATENTE ────────────────────────
 *
 * `TablaUnidades` dibuja las filas con la POSICIÓN como clave. Con el
 * «Consultando…» y la respuesta guardados adentro del botón, poner un filtro
 * mientras se espera al CENT haría que el mensaje del 448 aparezca en la fila
 * que quedó en su lugar —otra unidad, con total confianza—. Por eso el hook
 * guarda todo por patente, en la pantalla, y el botón sólo lo dibuja.
 *
 * ── UNA POR VEZ EN LA PANTALLA ───────────────────────────────────────────
 *
 * Mientras una patente está esperando, el hook no manda ningún otro pedido
 * —ni de ésa ni de otra— y los botones de las demás filas se apagan, con el
 * porqué en el `title`. Dos clics en la misma no son dos consultas, y bajar
 * por la columna apretando diez tampoco: eran diez pedidos a la vez, cada uno
 * una salida al CENT y uno de los cuatro hilos del bot ocupado esperándolo
 * (el webhook de WhatsApp y la portería usan los mismos). El bot tiene sus
 * propios frenos —cinco minutos por patente, y un tope entre todas—; esto es
 * para no mandarle lo que igual va a frenar. Lo normal es que el CENT
 * conteste en unos segundos, así que la siguiente fila espera eso.
 */

/* `alVolver(dominio, estado, respuesta)` se llama cada vez que vuelve un
 * estado —el nuevo, o el que había si el CENT no contestó—, para que la
 * pantalla lo pegue en la fila o en la ficha. `dominio` es el de la fila, tal
 * cual: es con lo que la pantalla la encuentra. */
export function useConsultasCENT(alVolver) {
  const [porPatente, setPorPatente] = useState({});
  const enCurso = useRef(new Set());
  const alVolverRef = useRef(alVolver);
  alVolverRef.current = alVolver;

  const consultar = useCallback(async (dominio) => {
    const d = limpiar(dominio);
    if (!d || enCurso.current.size > 0) return null;
    enCurso.current.add(d);
    setPorPatente((p) => ({ ...p, [d]: { yendo: true } }));

    let r;
    try {
      r = await consultarCENT(d);
    } catch {
      // `consultarCENT` no lanza; esto es por si mañana alguien lo cambia.
      r = { ok: false, error: "No se pudo consultar. Probá de nuevo en un rato." };
    } finally {
      enCurso.current.delete(d);
    }

    if (r?.estado) {
      try {
        alVolverRef.current?.(dominio, r.estado, r);
      } catch {
        // Pegar el estado es de la pantalla; si falla, el mensaje igual sale.
      }
    }
    setPorPatente((p) => ({ ...p, [d]: { yendo: false, ...mensajeDeConsulta(r) } }));
    return r;
  }, []);

  /* `otra`: hay una consulta en curso y no es ésta. El mensaje que la fila
   * ya tenía se sigue mostrando; sólo se apaga el botón. */
  const ocupado = useMemo(
    () => Object.values(porPatente).some((x) => x?.yendo),
    [porPatente],
  );
  const de = useCallback(
    (dominio) => {
      const propio = porPatente[limpiar(dominio)] || null;
      if (!ocupado || propio?.yendo) return propio;
      return { ...(propio || {}), otra: true };
    },
    [porPatente, ocupado],
  );

  /* El mismo objeto mientras no cambie nada: las pantallas lo ponen en las
   * dependencias de sus columnas, y uno nuevo en cada render las rearmaría
   * todas por cada tecla. */
  return useMemo(() => ({ de, consultar }), [de, consultar]);
}

const TITULO =
  "Le pregunta al CENT por esta patente ahora, sin esperar a la pasada del bot. Tarda unos segundos.";
const TITULO_OTRA = "Hay otra consulta al CENT en curso. Esperá a que termine y probá con ésta.";

/* `boton`: "enlace" en una fila de tabla (no compite con los datos), "boton"
 * en la ficha, igual que el «Consultar ahora» de Multas y Radicación. */
const ConsultarCENT = ({ dominio, consulta, onConsultar, boton = "enlace" }) => {
  const yendo = !!consulta?.yendo;
  const otra = !yendo && !!consulta?.otra;
  return (
    <span className="rto-cent">
      <button
        type="button"
        className={boton === "boton" ? "pf-mover__btn" : "rto-link"}
        disabled={yendo || otra || !onConsultar}
        aria-busy={yendo || undefined}
        aria-label={`${yendo ? "Consultando" : "Consultar ahora"} la RTO de ${dominio} en el CENT`}
        title={otra ? TITULO_OTRA : TITULO}
        onClick={() => onConsultar && onConsultar(dominio)}
      >
        {yendo ? "Consultando…" : "Consultar ahora"}
      </button>
      {!yendo && consulta?.texto && (
        <span role="status" className={"rto-cent__msg rto-cent__msg--" + consulta.tono}>
          {consulta.texto}
        </span>
      )}
    </span>
  );
};

export default ConsultarCENT;
